import React, { useState, useEffect } from 'react';
import OTPInput from 'react-otp-input';
import { CircleSpinner } from 'react-spinners-kit';
import { PaymentData, SimpliPayConfig, TransactionResult } from '../types';
import { createApiClient, verifyOtp, resendOtp } from '../utils/api';

interface OtpEntryProps {
  paymentData: PaymentData;
  config: SimpliPayConfig;
  transactionResponse: any;
  setStep: (step: any) => void;
  goBack: () => void;
  onClose: () => void;
  setError: (error: string) => void;
  onSuccess: (result: TransactionResult) => void;
}

const OtpEntry: React.FC<OtpEntryProps> = ({
  paymentData,
  config,
  transactionResponse,
  setStep,
  goBack,
  setError,
  onSuccess,
}) => {
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSubmit = async () => {
    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    setIsLoading(true);

    try {
      const client = createApiClient(config);
      const response = await verifyOtp(client, {
        transactionRef: paymentData.transactionRef,
        otp: otp,
      });

      if (response.success) {
        onSuccess({
          success: true,
          transactionRef: paymentData.transactionRef,
          message: response.message || 'Payment successful',
          amount: paymentData.amount,
          currency: paymentData.currency,
          paymentMethod: 'CARD',
        });
        setStep('success');
      } else {
        setError(response.message || 'OTP verification failed');
      }
    } catch (error: any) {
      setError(error?.response?.data?.message || 'OTP verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;

    setIsResending(true);
    try {
      const client = createApiClient(config);
      await resendOtp(client, paymentData.transactionRef);
      setResendTimer(60);
    } catch (error: any) {
      setError(error?.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="simplipay-step-container">
      <div className="simplipay-step-header">
        <button className="back-btn" onClick={goBack}>←</button>
        <h2>OTP Verification</h2>
        <button className="close-btn" onClick={goBack}>×</button>
      </div>

      {isLoading ? (
        <div className="simplipay-loading">
          <CircleSpinner size={40} color="#46246b" />
          <p>Verifying OTP...</p>
        </div>
      ) : (
        <>
          <p className="simplipay-description">
            {transactionResponse?.message || 'An OTP has been sent to your registered phone/email.'}
            <br /><br />
            Enter the code below.
          </p>

          <div className="simplipay-otp-container">
            <label>OTP</label>
            <OTPInput
              value={otp}
              onChange={setOtp}
              numInputs={6}
              renderInput={(props) => <input {...props} type="text" inputMode="numeric" />}
              containerStyle="simplipay-otp-box"
              inputStyle="simplipay-otp-input"
              shouldAutoFocus
            />
          </div>

          <button
            className="simplipay-button"
            onClick={handleSubmit}
            disabled={otp.length !== 6}
          >
            Verify
          </button>

          <p className="simplipay-resend">
            Didn't get the code?{' '}
            <button
              className="resend-btn"
              onClick={handleResend}
              disabled={resendTimer > 0 || isResending}
            >
              {isResending ? 'Sending...' : resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend'}
            </button>
          </p>
        </>
      )}

      <div className="simplipay-footer">
        <span>🔒 Secured by <strong>SimpliPay</strong></span>
      </div>
    </div>
  );
};

export default OtpEntry;
